import { useAccountReceivableGenerateGrouping } from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import {
  AccountReceivableGroupingInstallmentFormValues,
  AccountReceivableInstallmentGroupingItem,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { MathRound } from '@/utils/functions/MathRound';
import { DateTimeFormat } from '@/utils/moment';
import { Form, Modal, ModalProps, notification } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import AccountReceivableGroupingInstallmentForm from './AccountReceivableGroupingInstallmentForm';
import AccountReceivableGroupingInstallmentList from './AccountReceivableGroupingInstallmentList';

interface Props extends ModalProps {
  onCancel: VoidFunction;
  data?: {
    installmentIds: number[];
    totalValue: number;
  };
  cancelAfterSubmit: () => void;
}

const AccountReceivableGroupingInstallmentModal: React.FC<Props> = ({
  data,
  onCancel,
  cancelAfterSubmit,
  ...props
}) => {
  const [form] = Form.useForm<AccountReceivableGroupingInstallmentFormValues>();
  const firstDueDate = Form.useWatch('installmentsFirstDueDate', form);
  const daysInterval = Form.useWatch('intervalDays', form);
  const installmentQuantity = Form.useWatch('quantityInstallments', form);
  const [installments, setInstallments] = useState<
    AccountReceivableInstallmentGroupingItem[]
  >([]);

  const generateGrouping = useAccountReceivableGenerateGrouping();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        quantityInstallments: 1,
        intervalDays: 30,
        installmentsFirstDueDate: moment(),
      });
    }
  }, [data]);

  useEffect(() => {
    if (
      daysInterval &&
      firstDueDate &&
      installmentQuantity &&
      data?.totalValue
    ) {
      const installmentValue = MathRound(
        data.totalValue / installmentQuantity,
        2,
      );

      setInstallments(
        new Array(installmentQuantity)
          .fill(0)
          .reduce<AccountReceivableInstallmentGroupingItem[]>((acc, _, i) => {
            const dueDate =
              i === 0
                ? firstDueDate?.clone()
                : acc.at(-1)?.dueDate.clone().add(daysInterval, 'days');

            let installmentValueItem: number;

            if (i === installmentQuantity - 1) {
              installmentValueItem = MathRound(
                data.totalValue -
                  installmentValue * installmentQuantity +
                  installmentValue,
                2,
              );
            } else {
              installmentValueItem = installmentValue;
            }

            return [
              ...acc,
              {
                description: `${i + 1}ª Parcela`,
                dueDate: dueDate ?? moment(),
                installmentValue: installmentValueItem,
              },
            ];
          }, [] as AccountReceivableInstallmentGroupingItem[]),
      );
    }
  }, [data, firstDueDate, daysInterval, installmentQuantity]);

  function handleCancel() {
    onCancel();
    form.resetFields();
  }

  function handleCancelAfterSubmit() {
    form.resetFields();
    cancelAfterSubmit();
  }

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      if (data) {
        if (installments.every((installment) => !installment.documentTypeId)) {
          return notification.error({
            message: 'Informe o(s) tipo(s) de documento',
          });
        } else if (
          installments.every((installment) => !installment.bankAccountId)
        ) {
          return notification.error({
            message: 'Informe a(s) conta(s) bancária(s)',
          });
        }

        await generateGrouping.mutateAsync({
          ...values,
          installmentsFirstDueDate:
            values.installmentsFirstDueDate.format(DateTimeFormat),
          accountIds: data?.installmentIds,
          installmentFromGroupings: installments.map((item) => ({
            ...item,
            dueDate: item.dueDate.format(DateTimeFormat),
          })),
        });
        handleCancelAfterSubmit();
      }
    } catch {}
  }

  return (
    <Modal
      {...props}
      title="Gerar agrupamento de parcelas"
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Gerar agrupamento"
      confirmLoading={generateGrouping.isLoading}
      width={1200}
    >
      <AccountReceivableGroupingInstallmentForm form={form} />

      <AccountReceivableGroupingInstallmentList
        installments={installments}
        setInstallments={setInstallments}
      />
    </Modal>
  );
};

export default AccountReceivableGroupingInstallmentModal;
